import _ from 'underscore';
import { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Icon } from '@evertrue/et-components';
import useOutsideClick from 'hooks/use-outside-click';

// Component for individual recipient fields (to, cc, bcc)
const RecipientInput = ({ recipients, setFieldValue, fieldName, label, inputRef, onFocus, isActive = false }) => {
  const localInputRef = useRef(null);
  const containerRef = useRef(null);

  // Use the passed ref or the local ref
  const emailInputRef = inputRef || localInputRef;

  // Focus the input when isActive changes to true
  useEffect(() => {
    if (isActive && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [isActive, emailInputRef]);

  // Handle outside clicks using the custom hook
  useOutsideClick(containerRef, () => {
    if (emailInputRef.current && emailInputRef.current.value && isValid(emailInputRef.current.value)) {
      addEmail(emailInputRef.current.value);
    }
  });

  const removeEmail = async (email) => {
    // Make sure we're working with an array
    const currentRecipients = Array.isArray(recipients) ? recipients : [];
    const newRecipients = currentRecipients.filter((recipient) => recipient !== email);
    setFieldValue(fieldName, newRecipients);
  };

  const addEmail = (email) => {
    if (!email || email.trim() === '') return;

    const trimmedEmail = email.trim();
    // Make sure we're working with an array
    const currentRecipients = Array.isArray(recipients) ? recipients : [];

    // Check if email already exists (case insensitive)
    if (!currentRecipients.some((elem) => elem.toLowerCase() === trimmedEmail.toLowerCase())) {
      setFieldValue(fieldName, [...currentRecipients, trimmedEmail]);
    }

    if (emailInputRef.current) {
      emailInputRef.current.value = '';
    }
  };

  const isValid = (email) => {
    return email && email.trim() !== '' && _.validateEmail(email.trim());
  };

  const handleOnFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  const handleOnKeydown = (e) => {
    switch (e.key) {
      case 'Enter':
      case ',':
      case ' ':
        if (e.target.value && isValid(e.target.value)) {
          addEmail(e.target.value);
          e.preventDefault();
        }
        break;
      case 'Tab':
        if (e.target.value && isValid(e.target.value)) {
          addEmail(e.target.value);
        } else if (e.target.value) {
          e.target.value = '';
        }
        break;
      case 'Backspace':
        if (!e.currentTarget.value && recipients && recipients.length > 0) {
          // Make sure we're working with an array
          const currentRecipients = Array.isArray(recipients) ? recipients : [];
          setFieldValue(fieldName, currentRecipients.slice(0, -1));
        }
        break;
      default:
        break;
    }
  };

  // Ensure recipients is always an array
  const recipientsList = Array.isArray(recipients) ? recipients : [];

  return (
    <div className="email-form-header-field" style={{ width: '100%' }} ref={containerRef}>
      <label className="email-form--label">
        <strong className="email-form-header-label">{label ? `${label}:` : ''}</strong>
        <ul className="email-recipients-field">
          {recipientsList.map((recipient) => (
            <li className="email-recipients-field-tag" key={recipient}>
              {recipient}
              <Icon
                icon="cancel"
                size={1}
                after={true}
                className={'email-recipient-label-cross-icon'}
                onClick={(e) => {
                  e.stopPropagation();
                  removeEmail(recipient);
                }}
                style={{ margin: '5px', cursor: 'pointer' }}
              />
            </li>
          ))}
          <input
            ref={emailInputRef}
            type="text"
            onKeyDown={handleOnKeydown}
            onFocus={handleOnFocus}
            className={'email-recipients-field-input'}
            data-testid="recipient-field"
            name={`${label}-input`}
            placeholder={recipientsList.length === 0 ? 'Add recipient' : ''}
          />
        </ul>
      </label>
    </div>
  );
};

RecipientInput.propTypes = {
  recipients: PropTypes.array,
  setFieldValue: PropTypes.func.isRequired,
  fieldName: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  inputRef: PropTypes.object,
  onFocus: PropTypes.func,
  isActive: PropTypes.bool,
};

export default RecipientInput;
